import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { sanitizeSupabaseError } from '@/lib/errorHandler';
import { captureError } from '@/lib/sentry';
import { useOrganization } from '@/hooks/useOrganization';
import { useAuthContext } from '@/contexts/AuthContext';
import { isPaidPlan } from '@/lib/planos';
import {
  listRecorrencias,
  criarRecorrencia,
  atualizarRecorrencia,
  pausarRecorrencia,
  terminarRecorrencia,
  type CriarRecorrenciaInput,
  type AtualizarRecorrenciaInput,
} from '@/services/recorrencias.service';

export function useRecorrencias() {
  const { organizationId } = useOrganization();
  return useQuery({
    queryKey: ['recorrencias', organizationId],
    queryFn: listRecorrencias,
    enabled: true,
  });
}

export function useCriarRecorrencia() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { organization } = useAuthContext();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (dados: Omit<CriarRecorrenciaInput, 'orgId'>) => {
      // Plan gate (layer 2 of 3): refuse to create a recurrence on the free
      // Starter tier. Layer 1 is the disabled switch in the OS form; layer 3
      // is the RPC, which skips Starter orgs when generating.
      if (!isPaidPlan(organization?.plano)) {
        throw new Error(t('recurrences.form.lockedHint'));
      }
      return criarRecorrencia({ ...dados, orgId: requireOrgId() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recorrencias'] });
      toast.success(t('recurrences.toast.created'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useCriarRecorrencia' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useAtualizarRecorrencia() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, dados }: { id: string; dados: AtualizarRecorrenciaInput }) =>
      atualizarRecorrencia(id, dados),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recorrencias'] });
      toast.success(t('recurrences.toast.updated'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useAtualizarRecorrencia' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function usePausarRecorrencia() {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, ativa }: { id: string; ativa: boolean }) =>
      pausarRecorrencia(id, ativa),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['recorrencias'] });
      toast.success(
        variables.ativa ? t('recurrences.toast.resumed') : t('recurrences.toast.paused'),
      );
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'usePausarRecorrencia' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}

export function useTerminarRecorrencia() {
  const queryClient = useQueryClient();
  const { requireOrgId } = useOrganization();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (id: string) => terminarRecorrencia({ id, orgId: requireOrgId() }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recorrencias'] });
      toast.success(t('recurrences.toast.terminated'));
    },
    onError: (error: Error) => {
      captureError(error, { extra: { hook: 'useTerminarRecorrencia' } });
      toast.error(sanitizeSupabaseError(error));
    },
  });
}
